import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  signal,
  SimpleChanges,
} from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { ALL_COUNTRY_CODES, COUNTRY_ALPHA3_TO_ALPHA2 } from './utils/country-codes';
import { UNKNOWN_COUNTRY } from '../../../../services/language';

interface CountryOption {
  code: string;
  flag: string;
}

@Component({
  selector: 'app-country-select',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TranslatePipe, MatFormFieldModule, MatSelectModule],
  templateUrl: './country-select.html',
  styleUrl: './country-select.scss',
})
export class CountrySelectComponent implements OnInit, OnChanges, OnDestroy {
  private readonly allCountryOptions: CountryOption[] = ALL_COUNTRY_CODES.map((code) => ({
    code,
    flag: `/images/flags/${code}.svg`,
  }));

  readonly unknownOption: CountryOption = { code: UNKNOWN_COUNTRY, flag: '/images/flags/unknown.svg' };

  @Input() country: string | null = null;
  @Output() countryChange = new EventEmitter<string | null>();

  selectedOption = signal<CountryOption | undefined>(undefined);
  sortedOptions = signal<CountryOption[]>([]);

  private readonly displayNameCache = new Map<string, Intl.DisplayNames>();
  private langSubscription?: Subscription;

  constructor(private readonly translate: TranslateService) {}

  ngOnInit(): void {
    this.buildSortedOptions();
    this.langSubscription = this.translate.onLangChange.subscribe(() => {
      this.displayNameCache.clear();
      this.buildSortedOptions();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['country']) {
      this.syncSelectedOption();
    }
  }

  ngOnDestroy(): void {
    this.langSubscription?.unsubscribe();
  }

  private buildSortedOptions(): void {
    const locale = this.translate.getCurrentLang() ?? 'en';
    const sorted = [...this.allCountryOptions].sort((a, b) =>
      this.getCountryLabel(a.code).localeCompare(this.getCountryLabel(b.code), locale),
    );
    sorted.push(this.unknownOption);
    this.sortedOptions.set(sorted);
  }

  private syncSelectedOption(): void {
    if (!this.country) {
      this.selectedOption.set(undefined);
      return;
    }
    this.selectedOption.set(
      this.allCountryOptions.find((o) => o.code === this.country) ??
        (this.country === UNKNOWN_COUNTRY ? this.unknownOption : undefined),
    );
  }

  onFlagError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.src = '/images/flags/unknown.svg';
    img.onerror = null;
  }

  onSelectionChange(event: MatSelectChange): void {
    const code = event.value as string;
    this.selectedOption.set(
      this.allCountryOptions.find((o) => o.code === code) ??
        (code === UNKNOWN_COUNTRY ? this.unknownOption : undefined),
    );
    this.countryChange.emit(code);
  }

  getCountryLabel(countryCode: string): string {
    if (countryCode === UNKNOWN_COUNTRY) {
      return this.translate.instant('FORM_RE_REGISTRATION.FIELDS.COUNTRY.OTHER');
    }
    const alpha2 = COUNTRY_ALPHA3_TO_ALPHA2[countryCode];
    if (!alpha2) return countryCode;
    return this.getDisplayNamesForCurrentLanguage().of(alpha2) ?? countryCode;
  }

  private getDisplayNamesForCurrentLanguage(): Intl.DisplayNames {
    const locale = this.translate.getCurrentLang() ?? 'en';
    const cached = this.displayNameCache.get(locale);
    if (cached) return cached;
    const displayNames = new Intl.DisplayNames([locale], { type: 'region', fallback: 'code' });
    this.displayNameCache.set(locale, displayNames);
    return displayNames;
  }
}
